import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SourcesPanel } from '../sources/SourcesPanel';
import { LessonViewer } from '../lesson/LessonViewer';
import { DoubtsExplorePanel } from '../chat/DoubtsExplorePanel';
import { OutlineScopingDialog } from '../sources/OutlineScopingDialog';
import { useDataMode } from '@/contexts/DataModeContext';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import { normalizeSource } from '@/lib/sources/normalizeSource';
import { displayLessonTitle } from '@/lib/lesson-overview';
import { LessonCompactLayout } from './LessonCompactLayout';
import type { Source } from '@/data/fixtures';
import { useSourcesList } from '@/hooks/lesson/useSources';
import type { SourceItem as ApiSourceItem } from '@/lib/api-client';
import type { StepView, SourcesView } from '@/types/pipeline';

interface LessonLearningLayoutProps {
  lessonId?: string;
  contextData?: any;
  /**
   * Phase C - full lesson metadata (title, description, tags, settings,
   * pipeline_status). Threaded through to child panels so they can drive
   * skeleton/loaded/failed states from a single source of truth instead
   * of each polling its own GET endpoint.
   */
  lessonMetadata?: any;
  /**
   * Fully-resolved per-step view map from the pipeline-status poll. Each
   * StepView carries state + in_flow + on_demand + error, so panels read one
   * value to decide skeleton/content/failure/generate.
   */
  steps?: Record<string, StepView>;
  /** Source-ingestion rollup - drives the center build view + the left
   *  panel's ingestion progress. */
  sources?: SourcesView;
  /**
   * True once the lesson's `overall_status` is terminal (completed/failed).
   * Used by on-demand tabs to decide whether to show a "not part of this
   * flow" placeholder (during generation) vs. the Generate CTA.
   */
  lessonDone?: boolean;
  /**
   * Single source of truth for "the page has enough information to make a
   * meaningful state claim per tab." When false, every `<StepGate>`
   * downstream renders a neutral fetch-mode skeleton.
   */
  lessonReady?: boolean;
  /** Refetch pipeline-status (threaded to the build view's retry). */
  onRetry?: () => void;
}

export function LessonLearningLayout({
  lessonId,
  contextData: _contextData,
  lessonMetadata,
  steps,
  sources,
  lessonDone,
  lessonReady,
  onRetry,
}: LessonLearningLayoutProps) {
  const [leftPanelWidth] = useState(350);
  const [rightPanelWidth] = useState(380);
  const [isLeftPanelOpen] = useState(true);
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
  const [isRightPanelOpen] = useState(true);
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false);
  const [currentVersion, setCurrentVersion] = useState('v1.0');
  const [selectedSource, setSelectedSource] = useState<Source | null>(null);
  // ONE layout is mounted, not both with one hidden by CSS. A hidden copy is
  // still a live one: it fetched every artifact twice, polled twice, and - the
  // bug that forced this - kept its own Text sub-tab open, so a "text"
  // study-time row ran for the whole visit whatever the user was reading.
  // `lg` = 1024px. Below it the three fixed panels do not fit - at a
  // tablet's 820px the lesson between them was about 15px wide - so phones
  // AND tablets get the compact layout, with the side panels as sheets.
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  // Scope-first: a large source whose outline was extracted waits for the user
  // to pick which chapters to ingest. We own the dialog's open state here (not
  // in SourcesPanel) so the center panel's checkpoint and the chat panel can
  // reopen it — one shared source of truth across all three panels.
  const { isApi } = useDataMode();
  const { sources: apiSources } = useSourcesList(isApi ? lessonId : undefined);

  // The picked source is in the URL too (`?source=<id>`), like the tabs
  // (`useLessonTabParam`): a link, or a reload, opens the lesson with that
  // document showing. Picking one writes it — and the Appendix → Source Viewer
  // tab it shows in, so picking a source while on Glossary no longer leaves the
  // document out of sight.
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceParam = searchParams.get('source');
  useEffect(() => {
    if (!sourceParam || selectedSource?.id === sourceParam) return;
    const row = (apiSources as ApiSourceItem[]).find(
      s => s.source_id === sourceParam
    );
    if (row) setSelectedSource(normalizeSource(row));
  }, [sourceParam, apiSources, selectedSource?.id]);
  const selectSource = (s: Source | ApiSourceItem) => {
    const picked = normalizeSource(s);
    setSelectedSource(picked);
    setSearchParams(
      prev => {
        const p = new URLSearchParams(prev);
        p.set('source', picked.id);
        p.set('tab', 'reference');
        p.set('view', 'sources');
        // Below `lg` the source was picked in the sources sheet: close it
        // here, in this one write - a second `setSearchParams` in the same
        // tick would start from the stale URL and undo this one.
        p.delete('sheet');
        return p;
      },
      { replace: true }
    );
  };
  // All large sources still awaiting chapter selection (we scope them one at a
  // time — the picker targets the first, the rest queue behind it).
  const scopePending = isApi
    ? (apiSources as ApiSourceItem[]).filter(
        s => s.status === 'awaiting_scope' && s.outline
      )
    : [];
  const scopeSource = scopePending[0] ?? null;
  const scopePendingCount = scopePending.length;
  // A document waiting for chapter selection puts the chat to sleep, on a
  // first build and on an already-built lesson alike: the chat's "Choose
  // chapters" is the one way back to the picker (product decision — the slim
  // top banner that used to do this on a built lesson is gone). The centre
  // still keeps a built lesson's content on screen; only a first build, with
  // nothing to show, gives it to the checkpoint (`LessonViewer`).
  // Which source the user dismissed the dialog for. The dialog is open when a
  // scope source exists and hasn't been dismissed; reopening clears this.
  const [scopeDismissedId, setScopeDismissedId] = useState<string | null>(null);
  const scopeOpen = !!scopeSource && scopeDismissedId !== scopeSource.source_id;
  const openScope = () => setScopeDismissedId(null);
  const closeScope = () =>
    scopeSource && setScopeDismissedId(scopeSource.source_id);

  return (
    <div className='flex h-full flex-col overflow-hidden'>
      {/* Desktop Layout */}
      {isDesktop && (
        <div className='flex min-h-0 flex-1 gap-3 overflow-hidden px-6 py-6'>
          {/* Left Panel - Sources & Context */}
          {isLeftPanelOpen && (
            <div
              className={`animate-slide-in-left flex-shrink-0 overflow-hidden rounded-lg border border-border/60 bg-card/70 backdrop-blur-sm transition-all duration-300 ease-in-out dark:bg-card`}
              style={{
                width: isLeftPanelCollapsed ? 60 : leftPanelWidth,
                minWidth: isLeftPanelCollapsed ? 60 : leftPanelWidth,
              }}
            >
              {/* SourcesPanel owns its own inner PanelBoundary around the
                tab content so the header (back / version / collapse)
                stays visible during a crash - matches the Chat panel's
                pattern. */}
              <SourcesPanel
                lessonId={lessonId}
                isCollapsed={isLeftPanelCollapsed}
                onToggleCollapse={() =>
                  setIsLeftPanelCollapsed(!isLeftPanelCollapsed)
                }
                currentVersion={currentVersion}
                onVersionChange={setCurrentVersion}
                onSourceSelect={selectSource}
                lessonMetadata={lessonMetadata}
                steps={steps}
                sources={sources}
                lessonDone={lessonDone}
              />
            </div>
          )}

          {/* Center Panel - Lesson Content */}
          <div
            className={`animate-fade-in-up flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border/60 bg-card/70 backdrop-blur-sm dark:bg-card`}
          >
            <LessonViewer
              lessonId={lessonId}
              currentVersion={currentVersion}
              onVersionChange={setCurrentVersion}
              selectedSource={selectedSource}
              steps={steps}
              sources={sources}
              hasUserSources={lessonMetadata?.has_user_sources}
              lessonTitle={
                displayLessonTitle(lessonMetadata, steps?.overview?.state) ?? ''
              }
              lessonDone={lessonDone}
              lessonReady={lessonReady}
              onRetry={onRetry}
              scopeSource={scopeSource}
              scopePendingCount={scopePendingCount}
              onOpenScope={openScope}
            />
          </div>

          {/* Right Panel - Doubts & Explore */}
          {isRightPanelOpen && (
            <div
              className={`animate-slide-in-right flex-shrink-0 overflow-hidden rounded-lg border border-border/60 bg-card/70 backdrop-blur-sm transition-all duration-300 ease-in-out dark:bg-card`}
              style={{
                width: isRightPanelCollapsed ? 60 : rightPanelWidth,
                minWidth: isRightPanelCollapsed ? 60 : rightPanelWidth,
              }}
            >
              <DoubtsExplorePanel
                lessonId={lessonId}
                isCollapsed={isRightPanelCollapsed}
                onToggleCollapse={() =>
                  setIsRightPanelCollapsed(!isRightPanelCollapsed)
                }
                // Strip the display-only "v" prefix - the backend stores
                // and filters by the bare numeric ("1.0", "2.0").
                currentVersion={currentVersion?.replace(/^v/, '')}
                steps={steps}
                lessonDone={lessonDone}
                awaitingScope={!!scopeSource}
                onOpenScope={openScope}
              />
            </div>
          )}
        </div>
      )}

      {/* Phone + tablet layout */}
      {!isDesktop && (
        <LessonCompactLayout
          lessonId={lessonId}
          lessonMetadata={lessonMetadata}
          steps={steps}
          sources={sources}
          lessonDone={lessonDone}
          lessonReady={lessonReady}
          onRetry={onRetry}
          currentVersion={currentVersion}
          onVersionChange={setCurrentVersion}
          selectedSource={selectedSource}
          onSourceSelect={selectSource}
          apiSources={apiSources as ApiSourceItem[]}
          scopeSource={scopeSource}
          scopePendingCount={scopePendingCount}
          onOpenScope={openScope}
        />
      )}

      {/* Scope-first picker — mounted once at the layout level so both the
          center checkpoint and the chat panel share its open/close state. */}
      {/* Keyed by source so the next queued document gets a fresh picker.
          Section ids are `s{i}` in every outline, so without a remount the
          second book opened with the first book's chapters already ticked. */}
      {isApi && lessonId && scopeSource && (
        <OutlineScopingDialog
          key={scopeSource.source_id}
          lessonId={lessonId}
          source={scopeSource}
          isOpen={scopeOpen}
          onClose={closeScope}
        />
      )}
    </div>
  );
}
